/**
 * The repository history file (epic PI-SPEC-SW-E316, feature
 * PI-SPEC-SW-1662).
 *
 * Grooming, Formulator and product-area runs in SpecTree Toolbox have no
 * shell: Claude never runs git there. When a run needs recent history, the
 * Toolbox app reads it with git itself, before the run, and writes it as
 * {@link TOOLBOX_REPO_HISTORY_FILE} (`history.json`, mode 0600) in the run's
 * folder; the skill reads that file (inline `/spectree:ado-groom` gets it
 * through `--history`, and the Formulator through `historyFile` on its
 * `turn.json`).
 *
 * One entry per mapped repository copy: the commit it is at (`headCommit`)
 * and its newest commits, newest first, at most
 * {@link TOOLBOX_REPO_HISTORY_MAX_COMMITS} (`truncated` when there were
 * more), each with the files it touched, at most
 * {@link TOOLBOX_REPO_HISTORY_MAX_FILES_PER_COMMIT} (`filesTruncated` when
 * there were more). Author names only, never an email. The field rules reuse
 * the FogBugz "Look into the code" history file (`fogbugz/code.ts`).
 *
 * Depends on zod only.
 */
import { z } from "zod";
import { fogbugzCodeCommitShaSchema, fogbugzCodeHistoryCommitSchema, fogbugzCodeProjectSchema, fogbugzCodeRepoNameSchema, fogbugzCodeRepoPathSchema, } from "./fogbugz/code.js";
import { isoDateTime } from "./zod-helpers.js";
/** Version of the `history.json` file the app writes. */
export const TOOLBOX_REPO_HISTORY_SCHEMA_VERSION = 1;
/** The history file's name in the run folder. */
export const TOOLBOX_REPO_HISTORY_FILE = "history.json";
/** Most commits the file holds per repository. */
export const TOOLBOX_REPO_HISTORY_MAX_COMMITS = 100;
/** Most changed files the file lists per commit. */
export const TOOLBOX_REPO_HISTORY_MAX_FILES_PER_COMMIT = 50;
/** Most repositories the file holds. */
export const TOOLBOX_REPO_HISTORY_MAX_REPOS = 10;
/** A full git commit id: 40 lowercase hex characters. */
export const toolboxRepoHistoryShaSchema = z.string().regex(/^[0-9a-f]{40}$/, "sha must be 40 lowercase hex characters");
/**
 * A changed file: relative to the repository root, forward slashes only, no
 * `..` or `.` segment (the rules of `fogbugzCodeRepoPathSchema`, minus the
 * Windows separator, since git always reports `/`).
 */
export const toolboxRepoHistoryPathSchema = fogbugzCodeRepoPathSchema.refine((path) => !path.includes("\\"), {
    message: "path must use forward slashes",
});
/** One commit: who, when, what it said and the files it touched. */
export const toolboxRepoHistoryCommitSchema = z
    .object({
    sha: toolboxRepoHistoryShaSchema,
    /** Commit (committer) date, ISO 8601 with offset. */
    date: fogbugzCodeHistoryCommitSchema.shape.date,
    /** Author name, never an email. */
    author: fogbugzCodeHistoryCommitSchema.shape.author,
    subject: fogbugzCodeHistoryCommitSchema.shape.subject,
    files: z.array(toolboxRepoHistoryPathSchema).max(TOOLBOX_REPO_HISTORY_MAX_FILES_PER_COMMIT),
    /** True when the commit touched more than `TOOLBOX_REPO_HISTORY_MAX_FILES_PER_COMMIT` files. */
    filesTruncated: z.boolean(),
})
    .strict();
/** One repository copy: its mapped name, its Toolbox project, the commit it is at and its newest commits. */
export const toolboxRepoHistoryRepoSchema = z
    .object({
    name: fogbugzCodeRepoNameSchema,
    project: fogbugzCodeProjectSchema,
    headCommit: fogbugzCodeCommitShaSchema,
    /** True when the repository has more than `TOOLBOX_REPO_HISTORY_MAX_COMMITS` commits. */
    truncated: z.boolean(),
    commits: z.array(toolboxRepoHistoryCommitSchema).max(TOOLBOX_REPO_HISTORY_MAX_COMMITS),
})
    .strict();
/**
 * `history.json`: the repositories' recent history as the app read it with
 * git. `generatedAt` is "now" for judging whether a change is recent. Repo
 * names are unique.
 */
export const toolboxRepoHistoryFileSchema = z
    .object({
    schema: z.literal(TOOLBOX_REPO_HISTORY_SCHEMA_VERSION),
    generatedAt: isoDateTime({ offset: true }),
    repos: z
        .array(toolboxRepoHistoryRepoSchema)
        .max(TOOLBOX_REPO_HISTORY_MAX_REPOS)
        .refine((repos) => new Set(repos.map((repo) => repo.name)).size === repos.length, {
        message: "repository names must be unique",
    }),
})
    .strict();
//# sourceMappingURL=history.js.map